import apiClient from './apiService';

// Service for the unified task / to-do / CAPA-action entity (/api/v1/tasks).
// Distinct from the legacy taskService.ts which targets PRM project-tasks
// only via /api/v1/prm/projects/:id/tasks.
//
// Simplified workflow (new):
//   Doer submits (OPEN → IN_PROGRESS/PENDING/DONE)
//   Approver approves (IN_PROGRESS → PENDING/DONE) or rejects (→ OPEN)
//   Ratifier ratifies (PENDING → DONE) or rejects (→ OPEN)
//   Creator can cancel at any point.
// Status moves only through action endpoints — never through PUT /tasks/{id}.

export type TaskStatus =
    | 'OPEN' | 'IN_PROGRESS' | 'PENDING' | 'DONE' | 'CANCELLED'
    // Legacy statuses kept for backward-compat with existing DB rows:
    | 'VERIFIED' | 'ARCHIVED' | 'REVERTED' | 'REJECTED';

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type EffectivenessCheck = 'Effective' | 'Ineffective' | 'Pending';

export type TaskRole =
    | 'CREATOR' | 'DOER' | 'APPROVER' | 'VALIDATOR'
    | 'REVERT_RECIPIENT'; // legacy — kept for backward-compat

export interface TaskLink {
    // id is server-issued; undefined for not-yet-persisted links in the form.
    id?: number;
    type_id: number;
    type_code?: string | null;
    record_id: number;
    label?: string | null;
    code?: string | null;
    notes?: string | null;
}

export interface TaskLinkInput {
    type_id: number;
    record_id: number;
    notes?: string | null;
}

export interface Task {
    id: number;
    entity_id: number;
    name: string;
    description?: string | null;

    // Polymorphic source/link
    source_type_id?: number | null;
    source_record_id?: number | null;
    linked_type_id?: number | null;
    linked_record_id?: number | null;

    // Project association (optional)
    project_id?: number | null;
    project_name?: string | null;
    parent_id?: number | null;
    wbs_code?: string | null;

    // Role chain
    doer_id?: number | null;
    approver_id?: number | null;
    validator_id?: number | null;
    requester_id?: number | null;

    // Scheduling
    due_date?: string | null;
    grace_period_days: number;
    status: TaskStatus;
    priority?: TaskPriority | null;

    // Push-back state (most-recent snapshot)
    revert_notes?: string | null;
    reverted_to_id?: number | null;
    reverted_from_id?: number | null;
    reverted_on?: string | null;

    // Feedback / effectiveness
    feedback_required: boolean;
    feedback_text?: string | null;
    feedback_on?: string | null;
    effectiveness_required: boolean;
    effectiveness_check?: EffectivenessCheck | null;
    effectiveness_check_by_date?: string | null;

    // Server-resolved labels
    source_type_code?: string | null;
    source_label?: string | null;
    linked_type_code?: string | null;
    linked_label?: string | null;
    linked_entities?: TaskLink[] | null;
    doer_name?: string | null;
    approver_name?: string | null;
    validator_name?: string | null;
    my_role?: TaskRole | null;
    /** True when the requesting user created this task. Separate from my_role so
     *  creator+doer combos retain Cancel access even when my_role = 'DOER'. */
    is_creator?: boolean | null;

    is_active: number;
    created_by?: number | null;
    created_by_name?: string | null;
    created_on?: string | null;
    modified_by?: number | null;
    modified_on?: string | null;
}

export interface TaskCreate {
    name: string;
    description?: string;
    source_type_id?: number | null;
    source_type_code?: string | null;
    source_record_id?: number | null;
    linked_type_id?: number | null;
    linked_record_id?: number | null;
    project_id?: number | null;
    parent_id?: number | null;
    doer_id?: number | null;
    approver_id?: number | null;
    validator_id?: number | null;
    requester_id?: number | null;
    due_date?: string | null;
    grace_period_days?: number;
    priority?: TaskPriority | null;
    feedback_required?: boolean;
    feedback_text?: string | null;
    effectiveness_required?: boolean;
    effectiveness_check?: EffectivenessCheck | null;
    effectiveness_check_by_date?: string | null;
    /** When provided, REPLACES the existing link set on the task. */
    linked_entities?: TaskLinkInput[];
}

export type TaskUpdate = Partial<TaskCreate>;

export interface TaskListParams {
    source_type_code?: string;
    source_record_id?: number;
    linked_type_code?: string;
    linked_record_id?: number;
    doer_id?: number;
    approver_id?: number;
    validator_id?: number;
    requester_id?: number;
    reverted_to_id?: number;
    mine?: boolean;
    status?: TaskStatus;
    open_only?: boolean;
    due_before?: string;
    q?: string;
    project_id?: number;
    enrich?: boolean;
    limit?: number;
    offset?: number;
}

export const tasksService = {
    list: async (params: TaskListParams = {}): Promise<Task[]> => {
        const resp = await apiClient.get('/api/v1/tasks', { params });
        return resp.data?.data ?? [];
    },

    listPaged: async (params: TaskListParams = {}): Promise<{ data: Task[]; total: number }> => {
        const resp = await apiClient.get('/api/v1/tasks', { params });
        const data: Task[] = resp.data?.data ?? [];
        return { data, total: resp.data?.total ?? data.length };
    },

    get: async (id: number): Promise<Task> => {
        const resp = await apiClient.get(`/api/v1/tasks/${id}`);
        return resp.data?.data;
    },

    create: async (data: TaskCreate): Promise<Task> => {
        const resp = await apiClient.post('/api/v1/tasks', data);
        return resp.data?.data;
    },

    update: async (id: number, data: TaskUpdate): Promise<Task> => {
        const resp = await apiClient.put(`/api/v1/tasks/${id}`, data);
        return resp.data?.data;
    },

    remove: async (id: number): Promise<void> => {
        await apiClient.delete(`/api/v1/tasks/${id}`);
    },

    // ---- Workflow action endpoints ----

    /** Doer submits work with required remarks. Auto-advances:
     *  OPEN → IN_PROGRESS (if approver) | PENDING (if validator only) | DONE (if neither) */
    submit: async (id: number, remarks: string): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/submit`, { remarks });
        return resp.data?.data;
    },

    /** Approver approves with required remarks. IN_PROGRESS → PENDING (if validator) | DONE */
    approve: async (id: number, remarks: string): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/approve`, { remarks });
        return resp.data?.data;
    },

    /** Approver (from IN_PROGRESS) or Ratifier (from PENDING) rejects → back to OPEN. */
    reject: async (id: number, remarks: string): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/reject`, { remarks });
        return resp.data?.data;
    },

    /** Ratifier (validator) gives final sign-off with required remarks. PENDING → DONE. */
    ratify: async (id: number, remarks: string): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/ratify`, { remarks });
        return resp.data?.data;
    },

    cancel: async (id: number): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/cancel`);
        return resp.data?.data;
    },

    // ---- Multi-link management ----
    addLink: async (id: number, link: TaskLinkInput): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/links`, link);
        return resp.data?.data;
    },

    removeLink: async (id: number, linkId: number): Promise<Task> => {
        const resp = await apiClient.delete(`/api/v1/tasks/${id}/links/${linkId}`);
        return resp.data?.data;
    },

    // ---- Legacy workflow shortcuts (kept for back-compat) ----
    submitFeedback: async (id: number, feedback_text: string): Promise<Task> => {
        const resp = await apiClient.post(`/api/v1/tasks/${id}/feedback`, { feedback_text });
        return resp.data?.data;
    },

    submitEffectiveness: async (id: number, check: EffectivenessCheck): Promise<Task> => {
        const resp = await apiClient.post(
            `/api/v1/tasks/${id}/effectiveness`,
            { effectiveness_check: check }
        );
        return resp.data?.data;
    },
};

// =============================================================================
// Agenda links service (polymorphic links between agenda items and any entity)
// =============================================================================

export interface AgendaLink {
    id: number;
    meeting_id: number;
    agenda_instance_id: number;
    item_type_id: number;
    item_record_id: number;
    relationship_type?: string | null;
    notes?: string | null;
    // server-resolved
    item_type_code?: string | null;
    item_type_display?: string | null;
    item_label?: string | null;
    item_code?: string | null;
    icon?: string | null;
}

export interface AgendaLinkCreate {
    item_type_id: number;
    item_record_id: number;
    relationship_type?: string;
    notes?: string;
}

export const agendaLinksService = {
    list: async (meetingId: number, agendaId: number): Promise<AgendaLink[]> => {
        const resp = await apiClient.get(
            `/api/v1/gov/meetings/${meetingId}/agenda/${agendaId}/links`
        );
        return resp.data?.data ?? [];
    },
    create: async (meetingId: number, agendaId: number,
                   data: AgendaLinkCreate): Promise<AgendaLink> => {
        const resp = await apiClient.post(
            `/api/v1/gov/meetings/${meetingId}/agenda/${agendaId}/links`, data,
        );
        return resp.data?.data;
    },
    update: async (linkId: number, data: Partial<AgendaLinkCreate>): Promise<AgendaLink> => {
        const resp = await apiClient.put(`/api/v1/gov/agenda-links/${linkId}`, data);
        return resp.data?.data;
    },
    remove: async (linkId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/gov/agenda-links/${linkId}`);
    },
};

// All tasks for a meeting (across all its agenda items) — feeds the
// Meeting → Tasks tab.
export const meetingTasksService = {
    list: async (meetingId: number): Promise<Task[]> => {
        const resp = await apiClient.get(`/api/v1/gov/meetings/${meetingId}/tasks`);
        return resp.data?.data ?? [];
    },
};
